import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { withStyles } from 'Components/ComponentLibrary/Styles';
import Typography from 'Components/UI/Typography';
import FormHelperText from '../../../Core/Form/FormHelperText';
import FormControl from '../../../Core/Form/FormControl';
import FormGroup from '../../../Core/Form/FormGroup';
import CheckboxButton from '../../../Uncategorized/CheckboxButton';
import RadioButton from '../../../Uncategorized/RadioButton';

const styles = (theme) => ({
  root: {},
  inputLabel: {},
  radioGroup: {},
  uncheckedOption: {
    '&:focus-within, &:hover': {
      backgroundColor: theme.palette.facet.lightGray,
    },
  },
  checkedOption: {
    '&:hover, &:focus-within': {
      backgroundColor: theme.palette.facet.primaryBlueMuted2,
    },
  },
  helperText: {},
  groupHeader: {},
  groupedOption: {},
});

const isChecked = (answerValue, optionValue) => {
  if (Array.isArray(answerValue)) {
    return answerValue.includes(optionValue);
  }

  return answerValue === optionValue;
};

function BigPicklistControl({
  className,
  classes,
  error,
  helperText,
  multiple,
  testName,
  options,
  noOptionsPlaceholderText,
  onChange,
  value,
  ...rest
}) {
  const OptionControl = multiple ? CheckboxButton : RadioButton;
  const handleFormControlChange = optionValue => {
    return onChange(optionValue);
  };

  const handleMultipleChange = (optionValue, selected) => {
    const updatedValue = value.filter(v => {
      return v !== optionValue; // Start by removing option from selected
    });

    if (selected) {
      return [...updatedValue, optionValue];
    }

    return updatedValue;
  };

  return (
    <FormControl
      error={error}
      className={classNames(classes.root, className)}
      {...rest}
    >
      <FormGroup
        test={`big-picklist-control-${testName}`}
      >
        {options?.length > 0
          ? options.map((option) => {
            const checked = isChecked(value, option.value);
            return (
              <OptionControl
                autoFocus={'true'}
                test={`big-picklist-control-${option.value}-option`}
                checked={checked}
                className={classNames(classes.option, {
                  [classes.checkedOption]: checked,
                  [classes.uncheckedOption]: !checked,
                }, className)}
                key={option.value}
                value={option.value}
                onChange={
                  multiple ? evt => {
                    return onChange(
                      handleMultipleChange(option.value, evt.target.checked),
                      option.value,
                    );
                  }
                    : () => handleFormControlChange(option.value)
                }
                label={option.name}
              />
            );
          })
          : (
            <Typography align="center" color="primary">
              {Array.isArray(noOptionsPlaceholderText)
                ? noOptionsPlaceholderText.map(text => {
                  return (
                    <Typography
                      weight={'bold'}
                      color={'primary'}
                      variant={'body1'}
                      paragraph
                    >
                      {text}
                    </Typography>
                  );
                })
                : (<em>{noOptionsPlaceholderText}</em>)}
            </Typography>
          )}
      </FormGroup>
      {helperText && (
        <FormHelperText className={classes.helperText}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}

BigPicklistControl.defaultProps = {
  className: '',
  label: null, // Only here to satisfy Picklist.jsx
  multiple: false,
  shouldDisableOption: null,
  noOptionsPlaceholderText: 'No options',
};

BigPicklistControl.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  noOptionsPlaceholderText: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]),
  multiple: PropTypes.bool,
  testName: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]).isRequired,
  label: PropTypes.string,
  shouldDisableOption: PropTypes.bool,
};

export default withStyles(styles, { name: 'BigPicklistControl' })(BigPicklistControl);
